/* c8 ignore start */
import { IS_CLIENT } from '../../utils/support';
import { isUndefined } from '../../utils/unit';
import { ms } from './ms';
export var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["Silent"] = 0] = "Silent";
    LogLevel[LogLevel["Error"] = 1] = "Error";
    LogLevel[LogLevel["Warn"] = 2] = "Warn";
    LogLevel[LogLevel["Info"] = 3] = "Info";
    LogLevel[LogLevel["Debug"] = 4] = "Debug";
})(LogLevel || (LogLevel = {}));
export const LogLevelNameMap = Object.freeze({
    [LogLevel.Silent]: 'silent',
    [LogLevel.Error]: 'error',
    [LogLevel.Warn]: 'warn',
    [LogLevel.Info]: 'info',
    [LogLevel.Debug]: 'debug'
});
export const LogLevelColor = Object.freeze({
    [LogLevel.Silent]: 'white',
    [LogLevel.Error]: 'hsl(6, 58%, 50%)',
    [LogLevel.Warn]: 'hsl(51, 58%, 50%)',
    [LogLevel.Info]: 'hsl(219, 58%, 50%)',
    [LogLevel.Debug]: 'hsl(280, 58%, 50%)'
});
const HOST_COLORS_LOCAL_STORAGE_KEY = 'vds-debug-host-colors';
const colors = getSavedColors();
function getSavedColors() {
    var _a;
    if (IS_CLIENT && !isUndefined(window.localStorage)) {
        let colors = {};
        try {
            colors = JSON.parse((_a = localStorage.getItem(HOST_COLORS_LOCAL_STORAGE_KEY)) !== null && _a !== void 0 ? _a : '');
        }
        catch (_b) {
            // no-op
        }
        return new Map(Object.entries(colors));
    }
    return new Map();
}
function saveColors() {
    if (IS_CLIENT && !isUndefined(window.localStorage)) {
        const map = {};
        colors.forEach(function (value, key) {
            map[key] = value;
        });
        localStorage.setItem(HOST_COLORS_LOCAL_STORAGE_KEY, JSON.stringify(map));
    }
}
export class Logger {
    constructor(_host, _options = {}) {
        var _a, _b;
        this._host = _host;
        this._options = _options;
        this._hasStartedGroup = false;
        this._options.color = _options.color;
        if (!_options.color) {
            const color = (_a = colors.get(this.name)) !== null && _a !== void 0 ? _a : `hsl(${Math.random() * 360}, 55%, 70%)`;
            this._options.color = color;
            colors.set(this.name, color);
            saveColors();
        }
        this._logLevelConsumer = (_b = Logger._consumeLogLevel) === null || _b === void 0 ? void 0 : _b.call(Logger, _host);
        _host.addController(this);
    }
    get name() {
        var _a, _b, _c, _d;
        const owner = (_a = this._options.owner) !== null && _a !== void 0 ? _a : this._host;
        return (_d = (_c = (_b = owner.constructor) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : owner.name) !== null && _d !== void 0 ? _d : 'unknown';
    }
    get level() {
        var _a, _b;
        return isUndefined(this._options.logLevel)
            ? (_b = (_a = this._logLevelConsumer) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : LogLevel.Silent
            : this._options.logLevel;
    }
    set level(newLevel) {
        this._options.logLevel = newLevel;
    }
    hostConnected() {
        // no-op
    }
    error(...data) {
        this._log(LogLevel.Error, ...data);
    }
    warn(...data) {
        this._log(LogLevel.Warn, ...data);
    }
    log(...data) {
        this._log(LogLevel.Info, ...data);
    }
    info(...data) {
        this._log(LogLevel.Info, ...data);
    }
    debug(...data) {
        this._log(LogLevel.Debug, ...data);
    }
    errorGroup(snippetMessage, ...data) {
        return this._startGroup(LogLevel.Error, `🔥️ ${snippetMessage}`);
    }
    warnGroup(snippetMessage, ...data) {
        return this._startGroup(LogLevel.Warn, `🚨 ${snippetMessage}`);
    }
    logGroup(snippetMessage, ...data) {
        return this._startGroup(LogLevel.Info, snippetMessage);
    }
    infoGroup(snippetMessage, ...data) {
        return this._startGroup(LogLevel.Info, snippetMessage);
    }
    debugGroup(snippetMessage, ...data) {
        return this._startGroup(LogLevel.Debug, snippetMessage);
    }
    _startGroup(level, snippetMessage, parentStream) {
        if (this.level < level) {
            const blankStream = {
                append: () => blankStream,
                appendWithLabel: () => blankStream,
                group: () => blankStream,
                end: () => blankStream
            };
            return blankStream;
        }
        this._startCollapsedGroup(level, snippetMessage);
        this._hasStartedGroup = true;
        let groupEnded = false;
        const stream = {
            append: (...data) => {
                if (groupEnded)
                    return stream;
                console.log(...data);
                return stream;
            },
            appendWithLabel: (label, ...data) => {
                if (groupEnded)
                    return stream;
                this._labelledLog(label, ...data);
                return stream;
            },
            group: (snippetMessage) => {
                if (groupEnded)
                    return stream;
                console.groupCollapsed(snippetMessage);
                return this._startGroup(level, snippetMessage, stream);
            },
            end: () => {
                if (groupEnded)
                    return parentStream !== null && parentStream !== void 0 ? parentStream : stream;
                if (!parentStream) {
                    this._endGroup();
                }
                else {
                    console.groupEnd();
                }
                groupEnded = true;
                return parentStream !== null && parentStream !== void 0 ? parentStream : stream;
            }
        };
        return stream;
    }
    _endGroup() {
        this._hasStartedGroup = false;
        this._logTrace();
        console.groupEnd();
    }
    _getDebugTimeDiff() {
        var _a;
        const diff = performance.now() - ((_a = this._lastDebugTimestamp) !== null && _a !== void 0 ? _a : (this._lastDebugTimestamp = performance.now()));
        this._lastDebugTimestamp = performance.now();
        return ms(diff);
    }
    _getGroupName() {
        var _a, _b;
        const hostName = (_b = (_a = this._host.constructor) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'unknown';
        return this.name !== hostName
            ? `${hostName}::${this.name} `
            : `${hostName} `;
    }
    _startCollapsedGroup(level, snippetMessage) {
        var _a, _b;
        if (this._hasStartedGroup)
            return;
        const hostName = (_b = (_a = this._host.constructor) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'unknown';
        const groupName = this._getGroupName();
        const namespace = this._options.name ? `${String(this._options.name)}` : '';
        const logLevelName = LogLevelNameMap[level];
        const truncatedSnippetMessage = snippetMessage
            ? `${namespace.length > 0 ? ' ' : ''}${snippetMessage.substring(0, Math.min(snippetMessage.length, 100))}`
            : '';
        console.groupCollapsed(`%c${logLevelName}%c ${groupName}%c${namespace}%c${truncatedSnippetMessage}`, `background: ${LogLevelColor[level]}; color: white; padding: 1.2px 2.2px; border-radius: 2px; font-size: 10px;`, `color: ${this._options.color}; padding: 4px 0px;`, namespace.length > 0 ? 'color: hsl(0, 0%, 50%);' : '', 'color: hsl(0, 0%, 68%); font-size: 11px;');
        console.groupCollapsed(`%cHost element: %c${hostName}`, 'color: gray', '');
        console.log(this._host);
        console.groupEnd();
        this._labelledLog('Log level', LogLevelNameMap[level]);
        if (this._options.owner && this._options.owner !== this._host) {
            console.groupCollapsed(`%cOwner class: %c${this.name}`, 'color: gray', '');
            console.log(this._options.owner);
            console.groupEnd();
        }
        this._labelledLog('Time since last log', this._getDebugTimeDiff());
    }
    _log(level, ...data) {
        if (this.level < level)
            return;
        this._startCollapsedGroup(level, data.map(String).join(' '));
        this._labelledLog('Message', ...data);
        if (!this._hasStartedGroup) {
            this._logTrace();
            console.groupEnd();
        }
    }
    _labelledLog(label, ...data) {
        console.log(`%c${label}:`, 'color: gray', ...data);
    }
    _logTrace() {
        console.groupCollapsed('%cStack Trace', 'color: gray');
        console.trace();
        console.groupEnd();
    }
}
export class ElementLogger extends Logger {
    hostConnected() {
        super.hostConnected();
        this.debug('🧬 connected');
    }
    hostUpdated() {
        this.debug('🧬 updated');
    }
    hostDisconnected() {
        this.debug('🧬 disconnected');
    }
}
/* c8 ignore stop*/
